import React from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";

import { InfoIcon } from "./icons";

const HELP_LINK = /^(?:https?:\/\/(?:www\.)?kyou\.id)?\/help(\/.*)?$/;
const KYOU_LINK = /^https?:\/\/(?:www\.)?kyou\.id(\/|$)/;

/**
 * Links inside articles.
 *
 * - `/help/...` (and `https://kyou.id/help/...`) are this app: rendered as
 *   router links so they do not reload the page, and so ?webview=true survives
 *   (modules/history.js re-adds it on every push). `/help/static/...` files are
 *   left alone.
 * - A bare `#anchor` would resolve against `<base href="/help/">` in
 *   public/index.html and leave the article, so it becomes a hash on the
 *   current route.
 * - Other kyou.id pages (the return policy, T&C) open in place; everything
 *   else opens in a new tab.
 */
const MarkdownLink = ({ href = "", children, title }) => {
  const help = href.match(HELP_LINK);
  if (help && !/^\/static\//.test(help[1] || "")) {
    return (
      <Link to={help[1] || "/"} title={title}>
        {children}
      </Link>
    );
  }

  if (href.charAt(0) === "#") {
    return (
      <Link to={{ hash: href }} title={title}>
        {children}
      </Link>
    );
  }

  const isExternal = /^https?:\/\//.test(href) && !KYOU_LINK.test(href);
  return (
    <a href={href} title={title} target={isExternal ? "_blank" : undefined} rel={isExternal ? "noopener noreferrer" : undefined}>
      {children}
    </a>
  );
};

/**
 * Tables scroll inside their own box -- the page body never scrolls sideways
 * (spec section 7). Two-column tables get the spec's key/value layout: a 158px
 * label column, tabular figures, stacked below 640px.
 */
const MarkdownTable = ({ children, columnAlignment }) => {
  const columns = columnAlignment ? columnAlignment.length : 0;

  return (
    <div className="hc-table-scroll">
      <table className={`hc-table${columns === 2 ? " hc-table--2col" : ""}`}>{children}</table>
    </div>
  );
};

/**
 * Ordered lists are the spec's numbered steps; bullets stay bullets. The
 * numbers are drawn by a CSS counter, so a list that starts later (a "3." after
 * a paragraph) moves the counter instead of relying on `start`.
 */
const MarkdownList = ({ ordered, start, children }) =>
  ordered ? (
    <ol className="hc-steps" style={start > 1 ? { counterReset: `hc-step ${start - 1}` } : undefined}>
      {children}
    </ol>
  ) : (
    <ul className="hc-bullets">{children}</ul>
  );

/** `> text` in markdown is the spec's callout. */
const MarkdownCallout = ({ children }) => (
  <div className="hc-callout">
    <span className="hc-callout__icon">
      <InfoIcon />
    </span>
    <div className="hc-callout__body">{children}</div>
  </div>
);

const MarkdownImage = ({ src, alt, title }) => <img src={src} alt={alt || ""} title={title} loading="lazy" />;

/**
 * An article's markdown, rendered with the help-center components.
 *
 * `headings` is `extractHeadings(content)` (lib/article.js). Every heading is
 * drawn as an h2 -- the spec allows no h3 -- and takes the next precomputed id,
 * so the table of contents and the anchors cannot disagree.
 *
 * `escapeHtml={false}` stays: older articles carry raw HTML that must keep
 * working, notably the <span id="price-jpy"> anchors hanamaru's price
 * breakdown links to, an inline <img> and a <video>. The content is ours,
 * bundled at build time, not user input.
 */
const ArticleBody = ({ content, headings }) => {
  let headingIndex = 0;

  const renderers = {
    link: MarkdownLink,
    table: MarkdownTable,
    list: MarkdownList,
    blockquote: MarkdownCallout,
    image: MarkdownImage,
    heading: ({ children }) => {
      const heading = headings[headingIndex];
      headingIndex += 1;
      return <h2 id={heading ? heading.id : undefined}>{children}</h2>;
    }
  };

  return (
    <div className="hc-article__body">
      <ReactMarkdown source={content} escapeHtml={false} renderers={renderers} />
    </div>
  );
};

export default ArticleBody;
